import { FC, useCallback } from 'react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  AccountingType,
  base_currency_equivalent_calculation,
  BaseCurrencyEquivalentCalculationOption,
  system_currency
} from 'views/companies/settings/accounting/config';
import { FieldsStateType } from '@/common/types/form';
import { BaseCurrencyEquivalentCalculation, Currency } from '@/common/types/enums';
import { SingleValue } from 'react-select';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Select from '@/library/components/form/inputs/select';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatDisplayValue, formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type AccountingProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.accounting;
const CATEGORY_TITLE = 'Accounting';

const CALCULATION_OPTIONS = [
  {
    label: 'Regular (Base Currency Equivalent Amount = Local Currency Amount * Rate)',
    value: BaseCurrencyEquivalentCalculation.REGULAR
  },
  {
    label: 'Inverse (Base Currency Equivalent Amount = Local Currency Amount / Inverse Rate)',
    value: BaseCurrencyEquivalentCalculation.INVERSE
  }
];

const LABELS = {
  [system_currency]: 'Base Currency',
  [base_currency_equivalent_calculation]: 'Base Currency Equivalent Calculation'
};

const Accounting: FC<AccountingProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<AccountingType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();
  const selectedCurrencyLabel = fields[system_currency].value;
  const selectedCurrency: Currency | null = selectedCurrencyLabel
    ? { id: 0, label: selectedCurrencyLabel, name: selectedCurrencyLabel }
    : null;
  const selectedCalculationOption =
    CALCULATION_OPTIONS.find(
      option => option.value === fields[base_currency_equivalent_calculation].value
    ) ?? null;

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [system_currency]: companySettings.system_currency,
      [base_currency_equivalent_calculation]: companySettings.base_currency_equivalent_calculation
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as AccountingType | null,
    toPayload: values => [
      { key: system_currency, value: values[system_currency] },
      {
        key: base_currency_equivalent_calculation,
        value: values[base_currency_equivalent_calculation]
      }
    ],
    labels: LABELS,
    formatValue: (key, value) =>
      key === base_currency_equivalent_calculation
        ? formatOptionDisplay(value, CALCULATION_OPTIONS)
        : formatDisplayValue(value),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Currency"
        hidden={
          !isSettingVisible(CATEGORY_ID, system_currency) &&
          !isSettingVisible(CATEGORY_ID, base_currency_equivalent_calculation)
        }
        className="cpb-2 cmb-2"
      >
        <Row className="d-flex align-items-center gx-1">
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={system_currency}>
              <Select
                placeholder="Select currency"
                label={<InputLabel title="BASE CURRENCY" apiKey={system_currency} />}
                isAsync
                isClearable={false}
                memoizedRequestParams={{ path: '/lists', params: { list: 'currencies' } }}
                onChange={(option: SingleValue<Currency>) =>
                  option ? selectField(system_currency)(option.label) : null
                }
                getOptionValue={(option: Currency) => option.label}
                value={selectedCurrency}
              />
            </VisibleSetting>
          </Col>
        </Row>

        <Row className="d-flex align-items-center gx-1">
          <Col xs={3}>
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={base_currency_equivalent_calculation}
            >
              <Select
                className="mb-0"
                placeholder="Select calculation type"
                label={
                  <InputLabel
                    title="BASE CURRENCY EQUIVALENT CALCULATION"
                    apiKey={base_currency_equivalent_calculation}
                  />
                }
                onChange={(option: SingleValue<BaseCurrencyEquivalentCalculationOption>) =>
                  option ? selectField(base_currency_equivalent_calculation)(option.value) : null
                }
                getOptionLabel={(option: BaseCurrencyEquivalentCalculationOption) => option.label}
                getOptionValue={(option: BaseCurrencyEquivalentCalculationOption) => option.value}
                options={CALCULATION_OPTIONS}
                isAsync={false}
                isMulti={false}
                value={selectedCalculationOption}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>
    </Category>
  );
};

export default Accounting;
